import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatPrice } from "@/utils/formatPrice";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { format, eachWeekOfInterval, eachMonthOfInterval, endOfWeek, endOfMonth, subDays, differenceInDays, startOfMonth } from "date-fns";
import { sv } from "date-fns/locale";
import ExportDropdown from "./ExportDropdown";
import { useOrganization } from "@/hooks/useOrganization";
import type { ReportDateRange } from "./ReportDateRangePicker";

const COLORS = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

interface BookingRecord {
  status: string;
  user_id: string;
  start_time: string;
  price_total: number | null;
  [key: string]: unknown;
}

interface ProfileRecord {
  id: string;
  full_name: string | null;
  created_at: string;
  [key: string]: unknown;
}

interface Props {
  range: ReportDateRange;
  bookings: BookingRecord[];
  profiles: ProfileRecord[];
  userCount: number;
  compareEnabled: boolean;
}

export default function PlayersTab({ range, bookings, profiles, userCount, compareEnabled: _compareEnabled }: Props) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const walkInUserId = (organization as any)?.walk_in_user_id as string | undefined;
  const days = differenceInDays(range.to, range.from) + 1;
  const useMonthly = days > 90;

  const stats = useMemo(() => {
    // PR-AL: exclude walk-in sentinel user from player-level aggregations
    // (top players, churn, LTV, retention bands). Walk-in bookings still
    // count toward revenue/occupancy reports — those don't aggregate per
    // user_id, so they're unaffected.
    const confirmed = bookings.filter(
      (b) => b.status !== "cancelled" && (!walkInUserId || b.user_id !== walkInUserId),
    );
    const thirty = subDays(new Date(), 30);
    const ninety = subDays(new Date(), 90);
    const activeSet = new Set(confirmed.map((b) => b.user_id));
    const active30 = new Set(confirmed.filter((b) => new Date(b.start_time) >= thirty).map((b) => b.user_id));

    // Recurring vs one-time
    const countMap: Record<string, number> = {};
    confirmed.forEach((b) => { countMap[b.user_id] = (countMap[b.user_id] || 0) + 1; });
    const recurring = Object.values(countMap).filter((c) => c > 1).length;
    const oneTime = Object.values(countMap).filter((c) => c === 1).length;

    // Top 10
    const top10 = Object.entries(countMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([uid, cnt]) => {
        const p = profiles.find((pr) => pr.id === uid);
        const spent = confirmed.filter((b) => b.user_id === uid).reduce((s: number, b) => s + (b.price_total || 0), 0);
        return { name: p?.full_name || uid.slice(0, 8), bookings: cnt, spent };
      });

    // New players per period
    const intervals = useMonthly
      ? eachMonthOfInterval({ start: range.from, end: range.to })
      : eachWeekOfInterval({ start: range.from, end: range.to }, { weekStartsOn: 1 });

    const newPlayersData = intervals.map((start) => {
      const end = useMonthly ? endOfMonth(start) : endOfWeek(start, { weekStartsOn: 1 });
      const count = profiles.filter((p) => {
        const d = new Date(p.created_at);
        return d >= start && d <= end;
      }).length;
      return { period: useMonthly ? format(start, "MMM", { locale: sv }) : format(start, "d/M"), count };
    });

    // Inactive
    const inactive = userCount - new Set(
      confirmed.filter((b) => new Date(b.start_time) >= ninety).map((b) => b.user_id)
    ).size;

    // Retention / churn bands
    const sixty = subDays(new Date(), 60);
    const everBookedIds = new Set(confirmed.map((b) => b.user_id));
    const bookedLast30 = new Set(confirmed.filter((b) => new Date(b.start_time) >= thirty).map((b) => b.user_id));
    const bookedLast60 = new Set(confirmed.filter((b) => new Date(b.start_time) >= sixty).map((b) => b.user_id));
    const bookedLast90 = new Set(confirmed.filter((b) => new Date(b.start_time) >= ninety).map((b) => b.user_id));

    let churn30 = 0, churn60 = 0, churn90 = 0;
    everBookedIds.forEach((uid) => {
      if (!bookedLast30.has(uid)) churn30++;
      if (!bookedLast60.has(uid)) churn60++;
      if (!bookedLast90.has(uid)) churn90++;
    });

    // LTV
    const revenueByPlayer: Record<string, number> = {};
    confirmed.forEach((b) => {
      revenueByPlayer[b.user_id] = (revenueByPlayer[b.user_id] || 0) + (b.price_total || 0);
    });
    const playerRevenues = Object.values(revenueByPlayer);
    const totalRevenue = playerRevenues.reduce((s, v) => s + v, 0);
    const uniquePlayers = playerRevenues.length;
    const avgLtv = uniquePlayers > 0 ? totalRevenue / uniquePlayers : 0;
    const sortedRevenues = [...playerRevenues].sort((a, b) => a - b);
    const medianLtv = sortedRevenues.length > 0
      ? sortedRevenues.length % 2 === 0
        ? (sortedRevenues[sortedRevenues.length / 2 - 1] + sortedRevenues[sortedRevenues.length / 2]) / 2
        : sortedRevenues[Math.floor(sortedRevenues.length / 2)]
      : 0;

    // LTV segments
    const p10 = sortedRevenues[Math.floor(sortedRevenues.length * 0.1)] || 0;
    const p25 = sortedRevenues[Math.floor(sortedRevenues.length * 0.25)] || 0;
    const p90 = sortedRevenues[Math.floor(sortedRevenues.length * 0.90)] || 0;

    const segments = {
      vip: playerRevenues.filter(v => v >= p90).length,
      active: playerRevenues.filter(v => v >= p25 && v < p90).length,
      occasional: playerRevenues.filter(v => v >= p10 && v < p25).length,
      churnRisk: playerRevenues.filter(v => v < p10).length,
    };

    const top10Revenue = sortedRevenues.slice(Math.floor(sortedRevenues.length * 0.9)).reduce((s, v) => s + v, 0);
    const top10Share = totalRevenue > 0 ? Math.round((top10Revenue / totalRevenue) * 100) : 0;

    // LTV histogram buckets
    const ltvBuckets = [
      { label: "0-500", min: 0, max: 500 },
      { label: "500-2k", min: 500, max: 2000 },
      { label: "2k-5k", min: 2000, max: 5000 },
      { label: "5k-10k", min: 5000, max: 10000 },
      { label: "10k+", min: 10000, max: Infinity },
    ];

    const histogramData = ltvBuckets.map(bucket => ({
      range: bucket.label,
      count: playerRevenues.filter(v => v >= bucket.min && v < bucket.max).length,
    }));

    return {
      totalActive: activeSet.size,
      active30: active30.size,
      inactive: Math.max(0, inactive),
      recurring,
      oneTime,
      top10,
      newPlayersData,
      churn30,
      churn60,
      churn90,
      avgLtv,
      medianLtv,
      segments,
      top10Share,
      histogramData,
    };
  }, [bookings, profiles, userCount, range, useMonthly, walkInUserId]);

  const cohortData = useMemo(() => {
    // PR-AL: also exclude walk-in user from cohort retention analysis.
    const confirmed = bookings.filter(
      (b) => b.status !== "cancelled" && (!walkInUserId || b.user_id !== walkInUserId),
    );
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);

    // Get last 6 cohort months
    const cohortMonths: Date[] = [];
    for (let i = 0; i < 6; i++) {
      cohortMonths.push(new Date(sixMonthsAgo.getFullYear(), sixMonthsAgo.getMonth() + i, 1));
    }

    // Group profiles by registration month
    const profilesByCohort: Record<string, string[]> = {};
    profiles.forEach((p) => {
      const d = new Date(p.created_at);
      const key = format(startOfMonth(d), "yyyy-MM");
      if (!profilesByCohort[key]) profilesByCohort[key] = [];
      profilesByCohort[key].push(p.id);
    });

    // Build booking lookup: user_id -> set of booking months
    const bookingMonthsByUser: Record<string, Set<string>> = {};
    confirmed.forEach((b) => {
      if (!bookingMonthsByUser[b.user_id]) bookingMonthsByUser[b.user_id] = new Set();
      bookingMonthsByUser[b.user_id].add(format(startOfMonth(new Date(b.start_time)), "yyyy-MM"));
    });

    // Max columns = months from earliest cohort to now
    const maxMonths = 6;

    const rows = cohortMonths.map((cohortStart) => {
      const cohortKey = format(cohortStart, "yyyy-MM");
      const cohortLabel = format(cohortStart, "MMM yy", { locale: sv });
      const cohortUsers = profilesByCohort[cohortKey] || [];
      const total = cohortUsers.length;

      const months: (number | null)[] = [];
      for (let m = 0; m < maxMonths; m++) {
        const targetMonth = new Date(cohortStart.getFullYear(), cohortStart.getMonth() + m, 1);
        if (targetMonth > now) {
          months.push(null);
          continue;
        }
        const targetKey = format(targetMonth, "yyyy-MM");
        const count = cohortUsers.filter((uid) => bookingMonthsByUser[uid]?.has(targetKey)).length;
        months.push(count);
      }

      return { cohortLabel, total, months };
    });

    return { rows, maxMonths };
  }, [bookings, profiles, walkInUserId]);

  const getCohortCellColor = (count: number, total: number): string => {
    if (total === 0 || count === 0) return "";
    const pct = count / total;
    if (pct >= 0.6) return "bg-green-500 text-white";
    if (pct >= 0.4) return "bg-green-400 text-white";
    if (pct >= 0.2) return "bg-green-300";
    if (pct >= 0.1) return "bg-green-200";
    return "bg-green-100";
  };

  const pieData = [
    { name: t("reports.players.recurring"), value: stats.recurring },
    { name: t("reports.players.oneTime"), value: stats.oneTime },
  ];

  const exportData = () => stats.top10.map((p) => ({
    [t("reports.players.name")]: p.name,
    [t("reports.players.bookings")]: p.bookings,
    [t("reports.players.spent")]: p.spent,
  }));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ExportDropdown getData={exportData} filePrefix="spelare" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">{t("reports.players.active30d")}</p><p className="text-2xl font-bold">{stats.active30}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">{t("reports.players.totalActive")}</p><p className="text-2xl font-bold">{stats.totalActive}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">{t("reports.players.inactive90d")}</p><p className="text-2xl font-bold">{stats.inactive}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">{t("reports.players.registered")}</p><p className="text-2xl font-bold">{userCount}</p></CardContent></Card>
      </div>

      {/* New players chart */}
      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.players.newPlayersPerPeriod", { period: useMonthly ? t("reports.players.month") : t("reports.players.week") })}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={stats.newPlayersData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} name={t("reports.players.newPlayers")} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pie chart */}
        <Card>
          <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.players.recurringVsOneTime")}</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top 10 */}
        <Card>
          <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.players.top10MostActive")}</CardTitle></CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="space-y-2">
              {stats.top10.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="w-6 h-6 p-0 flex items-center justify-center text-xs">{i + 1}</Badge>
                    <span className="truncate max-w-[140px]">{p.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-medium">{p.bookings} bokn.</span>
                    <span className="text-muted-foreground ml-2 text-xs">{formatPrice(p.spent)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Retention / Churn */}
      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.players.retentionTitle")}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="grid grid-cols-3 gap-3">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">{t("reports.players.noBooking30d")}</p>
                <p className="text-2xl font-bold text-destructive">{stats.churn30}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">{t("reports.players.noBooking60d")}</p>
                <p className="text-2xl font-bold text-destructive">{stats.churn60}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">{t("reports.players.noBooking90d")}</p>
                <p className="text-2xl font-bold text-destructive">{stats.churn90}</p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* LTV */}
      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.players.ltvTitle")}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="grid grid-cols-2 gap-3">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">{t("reports.players.avgLtv")}</p>
                <p className="text-2xl font-bold">{formatPrice(stats.avgLtv)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">{t("reports.players.medianLtv")}</p>
                <p className="text-2xl font-bold">{formatPrice(stats.medianLtv)}</p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* LTV Segments */}
      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.players.ltvSegments")}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                <span className="text-xs text-muted-foreground">VIP (top 10%)</span>
              </div>
              <p className="text-xl font-bold">{stats.segments.vip}</p>
            </div>
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                <span className="text-xs text-muted-foreground">Aktiv (25-90%)</span>
              </div>
              <p className="text-xl font-bold">{stats.segments.active}</p>
            </div>
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                <span className="text-xs text-muted-foreground">Tillfällig (10-25%)</span>
              </div>
              <p className="text-xl font-bold">{stats.segments.occasional}</p>
            </div>
            <div className="rounded-lg border p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="text-xs text-muted-foreground">Churn-risk (bottom 10%)</span>
              </div>
              <p className="text-xl font-bold">{stats.segments.churnRisk}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {t("reports.players.top10Contribution", { share: stats.top10Share })}
          </p>
        </CardContent>
      </Card>

      {/* LTV Histogram */}
      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.players.ltvDistribution")}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats.histogramData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="range" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} name={t("reports.players.players")} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Cohort Analysis */}
      <Card>
        <CardHeader className="p-4 pb-2"><CardTitle className="text-sm">{t("reports.players.cohortAnalysis")}</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="text-left p-1 font-medium">{t("reports.players.cohort")}</th>
                <th className="text-center p-1 font-medium">{t("reports.players.count")}</th>
                {Array.from({ length: cohortData.maxMonths }, (_, i) => (
                  <th key={i} className="text-center p-1 font-medium">M{i}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohortData.rows.map((row) => (
                <tr key={row.cohortLabel}>
                  <td className="p-1 font-medium whitespace-nowrap">{row.cohortLabel}</td>
                  <td className="p-1 text-center text-muted-foreground">{row.total}</td>
                  {row.months.map((count, i) => (
                    <td
                      key={i}
                      className={`p-1 text-center rounded ${count !== null && row.total > 0 ? getCohortCellColor(count, row.total) : ""}`}
                    >
                      {count !== null ? count : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
